import React from 'react';
import { FormFieldDef, FormSection } from '../../types/formSchema';

interface Props {
  sections: FormSection[];
}

function isVisible(field: FormFieldDef, _allValues: Record<string, string>): boolean {
  if (!field.visibleWhen) return true;
  const { field: depField, equals } = field.visibleWhen;
  const val = _allValues[depField] ?? '';
  if (Array.isArray(equals)) return equals.includes(val);
  return val === equals;
}

function renderField(field: FormFieldDef) {
  const suffix = field.required ? ' (required)' : ' (optional)';

  switch (field.type) {
    case 'select':
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{field.label}{suffix}</div>
          <select style={selectStyle} disabled>
            <option>{field.options?.[0]?.label ?? '—'}</option>
          </select>
        </div>
      );
    case 'radio':
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{field.label}{suffix}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(field.options || []).slice(0, 4).map((opt) => (
              <label key={opt.value} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" disabled /> {opt.label}
              </label>
            ))}
            {(field.options || []).length > 4 && (
              <span style={{ fontSize: 10, color: '#9ca3af' }}>+{(field.options || []).length - 4} more</span>
            )}
          </div>
        </div>
      );
    case 'textarea':
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{field.label}{suffix}</div>
          <textarea rows={2} disabled placeholder={field.placeholder} style={textareaStyle} />
        </div>
      );
    case 'checkbox':
      return (
        <div style={fieldWrapStyle}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" disabled />
            {field.label}{suffix}
          </label>
        </div>
      );
    case 'number':
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{field.label}{suffix}</div>
          <input type="number" disabled placeholder={field.placeholder} style={inputStyle} />
        </div>
      );
    default:
      return (
        <div style={fieldWrapStyle}>
          <div style={fieldLabelStyle}>{field.label}{suffix}</div>
          <input disabled placeholder={field.placeholder} style={inputStyle} />
        </div>
      );
  }
}

export default function FormPreview({ sections }: Props) {
  const dummyValues: Record<string, string> = {};

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>Live Preview</div>
      <div style={formStyle}>
        {sections.map((section) => {
          const visibleFields = section.fields.filter((f) => isVisible(f, dummyValues));
          if (visibleFields.length === 0) return null;
          return (
            <div key={section.id} style={sectionWrapStyle}>
              {sections.length > 1 && (
                <div style={sectionTitleStyle}>{section.title}</div>
              )}
              {visibleFields.map((field) => (
                <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  borderTop: '1px solid #e5e7eb',
  background: '#f9fafb',
};

const headerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#6b7280',
  padding: '10px 16px 6px',
};

const formStyle: React.CSSProperties = {
  padding: '8px 16px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const sectionWrapStyle: React.CSSProperties = {
  marginBottom: 4,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid #e5e7eb',
};

const fieldWrapStyle: React.CSSProperties = {
  marginBottom: 8,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: '#374151',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 8px',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontSize: 12,
  background: '#fff',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical' as const,
};
